import { useContext, useEffect, useState } from "react";
import MainContext from "../../../context/mainContext";
import { Box, Button, CircularProgress, Paper } from "@mui/material";
import styles from "./Publication.module.scss";
import BackButton from "../../../components/BackButton";
import { useNavigate, useParams } from "react-router";
import workflowService from "../../../services/workflow.service";
import {
  WorkflowDetailModel,
  WorkflowType,
} from "../../../models/workflow.model";
import moment from "moment";
import { t } from "i18next";
import TitleValue from "./TitleValue";
import DocumentDownload from "../../../components/DocumentDownload";
import ModifyDialog from "../Modify/ModifyDialog";
import noCover from "../../../assets/images/no-cover.png";
import publicationDeleted from "../../../assets/images/publication-deleted.png";

const PublicationView: React.FC = () => {
  const { setBanner } = useContext(MainContext);
  const { service } = useContext(MainContext);
  const [loading, setLoading] = useState(false);
  const { ticketNumber } = useParams();
  const [downloading, setDownloading] = useState(false);
  const [workflow, setWorkflow] = useState<WorkflowDetailModel | null>(null);
  const navigate = useNavigate();
  const [showModify, setShowModify] = useState(false);
  const [modifyType, setModifyType] = useState<WorkflowType>("Modification");

  useEffect(() => {
    setBanner("rocky-peak-sunset.png");
  });

  useEffect(() => {
    if (ticketNumber) {
      setLoading(true);
      service(() =>
        workflowService.getWorkflowDetails(ticketNumber).then((result) => {
          setWorkflow(result);
          setLoading(false);
        })
      );
    }
  }, [ticketNumber, service]);

  const openSummary = (ticketNumber: string) => {
    navigate(`/summary/${ticketNumber}`);
  };

  const handleModifyAction = (type: WorkflowType) => {
    setModifyType(type);
    setShowModify(true);
  };

  const getImage = (workflow: WorkflowDetailModel) => {
    if (workflow.lastStatus === "Deleted")
      return <img src={publicationDeleted} alt="NoCover" />;

    return workflow?.imageUrl !== undefined && workflow?.imageUrl !== "" ? (
      <img
        src={workflow?.imageUrl}
        alt="Publication"
        onError={({ currentTarget }) => {
          currentTarget.onerror = null;
          currentTarget.src = noCover;
        }}
      />
    ) : (
      <img src={noCover} alt="NoCover" />
    );
  };

  return (
    <Box className={styles.main}>
      <Paper elevation={3} className={styles.basic}>
        <BackButton text="back" />
        <div className={styles.data}>
          <div className={styles.image}>
            {workflow === null || workflow === undefined ? (
              <CircularProgress className={styles.circular} />
            ) : (
              getImage(workflow)
            )}
          </div>
          <div className={styles.details}>
            <h3>{workflow?.title}</h3>
            <TitleValue title={t("author/s")} value={workflow?.authors} />
            <TitleValue title={t("topics")} value={workflow?.topics} />
            <TitleValue
              title={t("publicationDate")}
              value={
                workflow?.publicationDate
                  ? moment(workflow.publicationDate).format("MM/DD/YYYY")
                  : "N/A"
              }
            />
            <TitleValue title="" value={workflow?.abstract} />
            <Button
              className={styles.openSummaryLink}
              variant="text"
              onClick={() => openSummary(ticketNumber!)}
            >
              {t("openSummaryWorkflow")}
            </Button>
          </div>
          {workflow?.lastStatus != "Deleted" && workflow?.lastStatus != "" ? (
            <div className={styles.documents}>
              <p>{t("downloadPublication")}:</p>
              <div className={styles.links}>
                {workflow?.documents?.map((item) => (
                  <DocumentDownload
                    key={`document_download_${item.documentId}`}
                    documentId={item.documentId}
                    type="button"
                    text={t(`languages.${item.langCode}`) || item.langCode}
                  />
                ))}
              </div>
              <div className={styles.actions}>
                <Button
                  variant="text"
                  onClick={() => handleModifyAction("Modification")}
                >
                  {t("modifyPublication")}
                </Button>
                <Button
                  variant="text"
                  onClick={() => handleModifyAction("Delete")}
                >
                  {t("deletePublication")}
                </Button>
              </div>
            </div>
          ) : (
            <></>
          )}
        </div>
      </Paper>
      {workflow && (
        <ModifyDialog
          open={showModify}
          ticketNumber={workflow?.ticketNumber}
          cancelAction={() => setShowModify(false)}
          type={modifyType}
        />
      )}
    </Box>
  );
};

export default PublicationView;
